<template>
  <div class="sso-login-page">
    <div id="loadingBox">
      <Loader
        v-if="isLoading"
        :is-processing="true"
        :loader-text="loadingText"
        data-testid="sso-login-loader"
      />
      <div v-if="error" class="text-center">
        <p
          class="sso-login-page__error"
          role="alert"
          data-testid="sso-login-error"
        >
          {{ error }}
        </p>
        <button
          class="btn bg-gradient-blue text-white"
          @click="reinitiateLogin"
        >
          Login
        </button>
      </div>
    </div>
    <div id="redirectBox">
      <div
        v-if="mobileApp"
        class="d-flex flex-column flex-wrap align-items-center gap-4"
      >
        <div
          v-if="mobileApp === 'pass' || mobileApp === 'both'"
          class="d-flex flex-column px-4 flex-wrap align-items-center gap-4"
        >
          <div class="base-blue-text text-center" v-if="mobileApp === 'both'">
            If you are logging into the Securly Pass App please click here
          </div>
          <a
            class="btn bg-gradient-blue text-white"
            :href="redirectToMobileApp('pass')"
          >
            Go To PASS App
          </a>
        </div>
        <div
          v-if="mobileApp === 'visitor' || mobileApp === 'both'"
          class="d-flex flex-column px-4 flex-wrap align-items-center gap-4"
        >
          <div class="base-blue-text text-center" v-if="mobileApp === 'both'">
            If you are logging into the Securly Visitor App please click here
          </div>
          <a
            class="btn bg-gradient-blue text-white"
            :href="redirectToMobileApp('visitor')"
          >
            Go To Visitor App
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { onMounted, ref } from "vue"
import { useStore } from "vuex"
import { useRoute } from "vue-router"
import Loader from "@/v3components/shared/Loader/Loader.vue"
import localforage from "localforage"
import securlyPlatformConfig from "@/configs/securlyPlatformConfig.js"
import { LOGIN_ROUTE_PATH } from "@/configs/authLoginConfig.js"
import {
  completeWebLogin,
  loadSecurlyPlatformScript
} from "@/helpers/ssoLoginHelper.js"

export default {
  name: "SsoLogin",

  components: {
    Loader
  },

  setup() {
    const route = useRoute()
    const store = useStore()
    const loadingText = ref("Loading...")
    const isLoading = ref(true)
    const error = ref(null)
    const ssoRedirected = ref(true)
    const mobileApp = ref(null)
    const mobileRedirectQuery = ref(null)

    /**
     * Supported urls:
     * /login -> web securly-sso login
     * /login?mobile_redirect -> request from mobile app (Visitor or Pass old version)
     * /login?mobile_redirect&app=pass -> request from mobile app (Pass new version)
     * /login?mobile_redirect&app=visitor -> request from mobile app (Visitor new version)
     *  Once we get mobile_redirect, securly-sso login will happen and sso-login will be redirected to the
     * /login/mobile with the original query params, which will be used to redirect to the mobile app
     */

    onMounted(() => {
      if (route.query?.mobile_redirect) {
        // Retrieve the additional query params and value from the query string except mobile_redirect
        const additionalQueryParams = Object.entries(route.query)
          .filter(([key]) => key !== "mobile_redirect")
          .map(([key, value]) => `${key}=${value}`)
          .join("&")
        startSsoLogin(true, additionalQueryParams)
        return
      }

      if (route.query?.code) {
        const routeState = route.query?.state
        // check if the state is string or int then its value should be < 9999
        if (routeState < 9999) {
          // This is simulate login
          ssoRedirected.value = false
        } else {
          // This is normal login
          ssoRedirected.value = true
        }
      } else {
        ssoRedirected.value = true
      }

      if (ssoRedirected.value) {
        startSsoLogin()
        return
      }

      const state = route.query?.state
      isLoading.value = true
      const params = {
        ...route.query,
        redirectUri: getRedirectUri(),
        state,
        isMobile: route.params.source === "mobile"
      }
      startSimulateLogin(params)
    })

    const startSsoLogin = async (
      forMobile = false,
      additionalQueryParams = ""
    ) => {
      await localforage.clear().catch(() => null)

      let redirectUri = `${window.location.origin}` + LOGIN_ROUTE_PATH
      if (forMobile) {
        redirectUri = redirectUri + "/mobile?" + additionalQueryParams
      }

      let auth = null

      try {
        auth = await loadSecurlyPlatformScript()
        if (!auth) {
          error.value = "Unable to load sign-in."
          isLoading.value = false
          return
        }

        await auth.Init(
          securlyPlatformConfig.clientId,
          securlyPlatformConfig.issuer
        )

        try {
          await auth.Identify()
        } catch {
          loadingText.value = "Redirecting..."
          auth.Login(redirectUri)
          return
        }

        let loginForMobile =
          route.params.source === "mobile" || route.query?.mobile_redirect
        let additionalParams = { ...route.query }
        try {
          loadingText.value = "Signing in..."
          const response = await store.dispatch(
            "authentication/externalLogin",
            {
              isMobile: loginForMobile,
              securlysso: true
            }
          )

          isLoading.value = false
          if (loginForMobile) {
            afterLoginMobile(
              response?.data?.access_token,
              null,
              additionalParams
            )
          } else {
            completeWebLogin(store, response?.data)
          }
        } catch (err) {
          error.value = formatSsoLoginError(err)
          isLoading.value = false
        }
      } catch {
        window.location.reload()
      }
    }

    const afterLoginMobile = (token, err, additionalParams = null) => {
      mobileRedirectQuery.value = token ? "?token=" + token : "?error=" + err
      let skipAutoRedirect = additionalParams?.skip_click === "false"
      let app = "both"
      if (additionalParams?.app == "pass") {
        app = "pass"
      } else if (additionalParams?.app == "visitor") {
        app = "visitor"
      }
      if (!skipAutoRedirect && app !== "both") {
        window.location.href = redirectToMobileApp(app)
      }
      mobileApp.value = skipAutoRedirect ? app : null
    }

    const startSimulateLogin = async (params) => {
      try {
        const response = await store.dispatch(
          "authentication/externalLogin",
          params
        )
        const data = response?.data

        if (route.params.source === "mobile") {
          afterLoginMobile(data?.access_token)
        } else {
          completeWebLogin(store, data)
        }
        isLoading.value = false
      } catch (err) {
        isLoading.value = false
        if (route.params.source === "mobile") {
          afterLoginMobile(null, err?.message)
        } else {
          error.value = formatSsoLoginError(err)
        }
      }
    }

    const reinitiateLogin = () => {
      isLoading.value = true
      error.value = null
      loadingText.value = ""
      store.dispatch("authentication/unauthorize")
      store.dispatch("authentication/finishSecurlySession")
    }

    const formatSsoLoginError = (err) => {
      if (err?.message) {
        return err.message
      }
      if (Array.isArray(err?.errors) && err.errors.length) {
        return err.errors.join(", ")
      }
      return "Unable to sign in."
    }

    const getRedirectUri = (source = "web", type = null) => {
      const typePath = type ? `/${type}` : ""
      return `${document.location.origin}/${LOGIN_ROUTE_PATH}/${source}${typePath}`
    }

    const redirectToMobileApp = (app) => {
      if (app === "pass") {
        return (
          import.meta.env.VITE_MOBILE_URL + "login" + mobileRedirectQuery.value
        )
      } else if (app === "visitor") {
        return (
          import.meta.env.VITE_VISITOR_MOBILE_URL +
          "login" +
          mobileRedirectQuery.value
        )
      } else {
        console.error("Invalid app") //eslint-disable-line
      }
    }

    return {
      isLoading,
      loadingText,
      error,
      mobileApp,
      reinitiateLogin,
      redirectToMobileApp
    }
  }
}
</script>

<style scoped lang="scss">
@import "src/assets/scss/_variables.scss";

.sso-login-page {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
}

.sso-login-page__error {
  max-width: 420px;
  margin: 0 1rem;
  text-align: center;
  color: $form-red;
}
</style>
